import React from 'react';
import { useLocation } from 'react-router-dom';
import { AccountCard } from '@parsina/framework';
import { C, GRADIENT_SIDEBAR } from '../../constants/theme';
import logo from 'url:../../assets/logo-white.png';
import { logout } from '../../lib/auth.js';
import { accountUrl, enterClinicPanel } from '../../lib/runtimeEnv.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useMe } from '../../hooks/useMe.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { useCreditBalance } from '../../hooks/useCredits.js';
import { creditLevel, CREDIT_LEVEL_META } from '../../constants/credit.js';
import { fmtCoins } from '../../constants/creditPricing.js';
import { NAV_ITEMS, activeNavFor } from '../../constants/nav.js';

/*
 * RTL layout: the sidebar is on the RIGHT of the screen.
 * When it collapses, `overflow: hidden` clips from the LEFT — so anything that
 * must stay visible (the toggle, the icons, the avatar) has to be anchored to
 * the RIGHT, i.e. come FIRST in RTL flex order. Every rule below follows from
 * that one sentence.
 */
const MINI = 68;   // collapsed width
const FULL = 260;  // expanded width

// The destination list is `src/constants/nav.js` — ONE copy, shared with the
// mobile shell's bottom bar so the two cannot drift. Nothing about a
// destination is declared here any more; this file only draws it as a rail.

// `activeNavFor` lives in constants/nav.js beside the destination list, and is
// re-exported here for the call sites that knew it by this name.
export { activeNavFor };

// Glyphs for the AccountCard menu. App-local, like the destination icons: the
// kit owns the menu's shape, not its destinations, so it owns none of their
// icons either. Identical set to the doctor panel's, so a dentist who uses both
// is not relearning the same four rows.
const ICON = {
  account: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" /></svg>,
  clinic: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" /></svg>,
  // Not mirrored: the arrow points out of a door, not along the reading direction.
  logout: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" /></svg>,
};

const DentistLayout = ({ children }) => {
  const { t, formatNumber } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();
  const { me } = useMe();
  const { scopeClinics, activeId, setActiveId } = useClinicScope();

  // The dentist's own پارسه, for the AccountCard below. The SAME wallet the
  // doctor panel spends — one person, one balance.
  const { balance } = useCreditBalance();
  // The level comes from the framework's one credit scale, so this panel and the
  // doctor's cannot disagree about what «کم» means.
  const balanceLevel = creditLevel(balance);

  // Which clinic «رفتن به پنل کلینیک» lands on: the one currently scoping this
  // panel, so the dentist arrives where they were already working. Falls back to
  // the first membership when the scope has not resolved yet, and is null — so
  // the row is not rendered — when there is no clinic to enter at all.
  const clinicTarget = React.useMemo(
    () => scopeClinics.find((m) => m.clinicId === activeId) || scopeClinics[0] || null,
    [scopeClinics, activeId],
  );

  // This layout is now the DESKTOP surface only — App.jsx swaps it for
  // MobileShell at ≤768px, so the collapse below is about a narrow LAPTOP (a
  // chairside window between 768 and 1100px), not about a phone. A phone gets
  // the bottom bar, not a 68px rail eating 19% of a 360px viewport.
  const [collapsed, setCollapsed] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1100,
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1100px)');
    const onChange = (e) => setCollapsed(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const current = activeNavFor(location.pathname);

  // Labels live in the DOM always and fade, so the width transition stays smooth.
  const fade = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.22s ease',
    pointerEvents: collapsed ? 'none' : 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  return (
    <div style={{ display: 'flex', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {/* ── Sidebar (RIGHT in RTL) ── */}
      <div
        style={{
          width: collapsed ? MINI : FULL,
          flexShrink: 0,
          background: GRADIENT_SIDEBAR,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
          zIndex: 10,
        }}
      >
        {/* Header: toggle FIRST → rightmost → never clipped. */}
        <div
          style={{
            height: 68,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <div style={{ width: MINI, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? t('nav.sidebar.expand') : t('nav.sidebar.collapse')}
              aria-label={collapsed ? t('nav.sidebar.expand') : t('nav.sidebar.collapse')}
              style={{
                width: 36,
                height: 36,
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="18"
                height="18"
                style={{
                  transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
                  // The glyph points at the sidebar's own edge, so it mirrors
                  // with the layout as well as rotating with the toggle.
                  transform: `scaleX(var(--dir-flip, 1)) rotate(${collapsed ? 0 : 180}deg)`,
                }}
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              paddingInlineStart: 4,
              paddingInlineEnd: 8,
              ...fade,
            }}
          >
            <img
              src={logo}
              alt=""
              style={{ height: '2.2rem', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
            />
            <div>
              <div style={{ color: 'white', fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap' }}>
                {t('nav.brand.name')}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, whiteSpace: 'nowrap' }}>
                {t('nav.brand.panel')}
              </div>
            </div>
          </div>
        </div>

        {/* ── Nav ── */}
        <nav
          aria-label={t('nav.landmark.primary')}
          style={{ flex: 1, paddingTop: 10, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {NAV_ITEMS.map((item) => {
            const active = current === item.id;
            const { Icon } = item;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(item.id)}
                title={collapsed ? t(item.labelKey) : undefined}
                aria-current={active ? 'page' : undefined}
                style={{
                  width: 'calc(100% - 20px)',
                  margin: '2px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0,
                  border: 'none',
                  borderRadius: 10,
                  background: active ? C.navy : 'transparent',
                  boxShadow: active ? '0 2px 8px rgba(0,0,0,0.22)' : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.18s, box-shadow 0.18s',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = 'transparent';
                }}
              >
                {/* Icon first in RTL → rightmost → never clipped. */}
                <span
                  style={{
                    width: MINI - 20,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '11px 0',
                    color: active ? 'white' : 'rgba(255,255,255,0.55)',
                  }}
                >
                  <Icon size={20} aria-hidden />
                </span>
                <span
                  style={{
                    ...fade,
                    color: active ? 'white' : 'rgba(255,255,255,0.55)',
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {t(item.labelKey)}
                </span>
              </button>
            );
          })}
        </nav>

        {/* ── Clinic switcher ──
            Shown only when there is a choice to make. With one membership the
            scope is not a decision, and with none the empty states on بیماران
            explain it far better than a disabled dropdown would.

            `scopeClinics`, not `clinics`: a membership the clinic has not
            accepted yet cannot scope anything (the BFF answers CLINIC_FORBIDDEN
            for it), so listing it here would offer an option that breaks every
            screen and then snaps back to another clinic. Its status is shown on
            پروفایل, which is where it can be acted on. */}
        {scopeClinics.length > 1 && !collapsed && (
          <div style={{ padding: '0 14px 10px', flexShrink: 0 }}>
            <label
              htmlFor="clinic-scope"
              style={{
                display: 'block',
                fontSize: 10.5,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.45)',
                marginBottom: 5,
              }}
            >
              {t('nav.clinic.label')}
            </label>
            <select
              id="clinic-scope"
              value={activeId || ''}
              onChange={(e) => setActiveId(e.target.value)}
              style={{
                width: '100%',
                height: 34,
                borderRadius: 9,
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: 12.5,
                padding: '0 8px',
              }}
            >
              {scopeClinics.map((m) => (
                <option key={m.clinicId} value={m.clinicId} style={{ color: C.textPrimary }}>
                  {m.clinic?.name || m.clinicId}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ── AccountCard ──
            The kit's block, identical in shape to the doctor and clinic panels:
            the پارسه balance, who is signed in, and one menu holding every
            account-level destination.

            THE BALANCE IS NEW HERE. This rail had none — the dentist could only
            learn their balance by opening «اعتبار» — and it is the same wallet
            the doctor panel spends: there is deliberately no `dentist`
            owner_type, so one person has one balance and a top-up made in
            either panel pays for an AI call made in the other.

            «رفتن به پنل کلینیک» is absent — not disabled — when this dentist
            belongs to no clinic they may enter. `scopeClinics` is the right
            list: it already excludes a membership the clinic has not accepted,
            for which the BFF answers CLINIC_FORBIDDEN, so the row is never
            offered to someone the destination would turn away. */}
        <AccountCard
          data-testid="account-card"
          collapsed={collapsed}
          menuLabel={t('nav.account.menuLabel')}
          user={{ name: me?.fullName || '—', subtitle: me?.specialty || '' }}
          credit={balance === null ? null : {
            // Already localised by the app — the kit renders digits verbatim.
            amount: fmtCoins(balance, formatNumber),
            level: balanceLevel,
            unit: t('credits.unit'),
            label: t('nav.credits.label'),
            levelLabel: balanceLevel ? t(CREDIT_LEVEL_META[balanceLevel].shortLabelKey) : '',
            onClick: () => navigate('credits'),
          }}
          onTopUp={() => navigate('credits')}
          topUpLabel={t('nav.credits.topUp')}
          items={[
            {
              key: 'account',
              // Name, password and language belong to حساب پارسینا — one account
              // backs every panel, so an editor here would be a second writer
              // against the same person.
              label: t('nav.account.parsina'),
              description: t('nav.account.parsinaHint'),
              icon: ICON.account,
              href: accountUrl('/'),
              external: true,
              'data-testid': 'sidebar-account',
            },
            // Neither «پروفایل» nor «اعتبار» gets a row: both are visible nav
            // destinations, and a menu copy of a row one press away in the rail
            // is noise. The balance card above is the entry to اعتبار; this menu
            // carries only what the rail cannot.
            clinicTarget && {
              key: 'clinic',
              label: t('nav.clinicSwitch.label'),
              description: clinicTarget.clinic?.name || '',
              icon: ICON.clinic,
              onClick: () => enterClinicPanel(clinicTarget.clinicId),
              'data-testid': 'switch-to-clinic',
            },
            {
              key: 'logout',
              label: t('nav.sidebar.logout'),
              icon: ICON.logout,
              tone: 'destructive',
              separatorBefore: true,
              onClick: () => logout(),
            },
          ].filter(Boolean)}
        />
      </div>

      {/* ── Page content (LEFT in RTL) ──
          A <main>, not a <div>: with the rail named above, the two landmarks are
          what a screen reader user jumps between instead of tabbing past three
          nav rows on every page. */}
      <main
        aria-label={t('nav.landmark.content')}
        style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {children}
      </main>
    </div>
  );
};

export default DentistLayout;
